import React, { useRef, useState } from 'react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { VisibilityState } from '@tanstack/react-table'
import type { ColumnDefinition } from '../types'
import { getColumnLabel } from '../types'
import * as s from '../Table.css'
import { TablePopover } from './TablePopover'
import { Modal } from '../../Modal'
import { Button } from '../../Button'
import { useT } from '../../../../app/lib/i18n'
import { getPortalContainer } from '../../../lib/portalContainer'

function Icon({ name, size = 14 }: { name: string; size?: number }) {
  const p = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }
  if (name === 'grid')
    return (
      <svg {...p}>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    )
  if (name === 'filter')
    return (
      <svg {...p}>
        <path d="M4 6h16M7 12h10M10 18h4" />
      </svg>
    )
  return null
}

function SortableConfigRow({
  id,
  label,
  checked,
  onToggle,
}: {
  id: string
  label: string
  checked: boolean
  onToggle: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id })

  return (
    <label
      ref={setNodeRef}
      className={`${s.configRow}${isDragging ? ` ${s.configRowDragging}` : ''}`}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <span
        className={s.configDragHandle}
        {...attributes}
        {...listeners}
        aria-hidden
        onClick={(e) => e.stopPropagation()}
      >
        ⠿
      </span>
      <input
        type="checkbox"
        className={s.checkbox}
        checked={checked}
        onChange={() => onToggle()}
        onClick={(e) => e.stopPropagation()}
      />
      <span style={{ flex: 1 }}>{label}</span>
    </label>
  )
}

/** Icon-only dashed chip that opens the quick-filter visibility menu. */
export function FiltersConfigButton<TRow>({
  filterColumns,
  visibleFilterIds,
  onVisibleFilterIdsChange,
  presentation = 'menu',
}: {
  filterColumns: ColumnDefinition<TRow>[]
  visibleFilterIds: string[]
  onVisibleFilterIdsChange: (ids: string[]) => void
  /** Open as a modal on narrow chrome instead of a popover. */
  presentation?: 'menu' | 'modal'
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const useModal = presentation === 'modal'

  if (filterColumns.length === 0) return null

  const toggleFilter = (id: string) => {
    const set = new Set(visibleFilterIds)
    if (set.has(id)) set.delete(id)
    else set.add(id)
    onVisibleFilterIdsChange([...set])
  }

  const list = (
    <div className={useModal ? undefined : s.popoverConfigScroll}>
      {!useModal ? (
        <div className={s.popoverPanelTitle}>{t('shared.table.filters')}</div>
      ) : null}
      {filterColumns.map((c) => {
        const isVisible = visibleFilterIds.includes(c.id)
        return (
          <label key={c.id} className={s.popoverOption}>
            <input
              type="checkbox"
              className={s.checkbox}
              checked={isVisible}
              onChange={() => toggleFilter(c.id)}
              onClick={(e) => e.stopPropagation()}
            />
            <span>{c.filterLabel ?? getColumnLabel(c)}</span>
          </label>
        )
      })}
    </div>
  )

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`${s.filterConfigBtn}${open ? ' is-active' : ''}`}
        aria-label={t('shared.filter.setFilters')}
        title={t('shared.filter.setFilters')}
        aria-expanded={open}
        aria-haspopup={useModal ? 'dialog' : 'menu'}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="filter" size={14} />
      </button>
      {useModal ? (
        <Modal
          open={open}
          setOpen={setOpen}
          minWidth="360px"
          maxWidth="420px"
          minHeight="auto"
          maxHeight="85vh"
          container={getPortalContainer()}
        >
          <Modal.Header>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
              Set Filters
            </h3>
          </Modal.Header>
          <Modal.Body>{list}</Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              color="primary"
              variant="solid"
              fullWidth
              onClick={() => setOpen(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      ) : (
        <TablePopover
          anchorRef={btnRef}
          open={open}
          onClose={() => setOpen(false)}
          align="start"
          className={s.popoverConfig}
        >
          {list}
        </TablePopover>
      )}
    </>
  )
}

/** Icon-only dashed chip that opens the column visibility / order menu. */
export function ColumnsConfigButton<TRow>({
  columns,
  columnVisibility,
  columnOrder,
  onColumnVisibilityChange,
  onColumnOrderChange,
  presentation = 'menu',
}: {
  columns: ColumnDefinition<TRow>[]
  columnVisibility: VisibilityState
  columnOrder: string[]
  onColumnVisibilityChange: (v: VisibilityState) => void
  onColumnOrderChange: (order: string[]) => void
  /** Open as a modal on narrow chrome instead of a popover. */
  presentation?: 'menu' | 'modal'
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const useModal = presentation === 'modal'

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  const toggleColumn = (id: string) => {
    onColumnVisibilityChange({
      ...columnVisibility,
      [id]: columnVisibility[id] === false,
    })
  }

  const orderedForConfig = columnOrder
    .map((id) => columns.find((c) => c.id === id))
    .filter((c): c is ColumnDefinition<TRow> => Boolean(c))
    .filter((c) => c.enableHiding !== false)

  if (orderedForConfig.length === 0) return null

  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const ids = orderedForConfig.map((c) => c.id)
    const oldIndex = ids.indexOf(String(active.id))
    const newIndex = ids.indexOf(String(over.id))
    if (oldIndex === -1 || newIndex === -1) return
    const reordered = arrayMove(ids, oldIndex, newIndex)
    const hidden = columnOrder.filter((id) => !ids.includes(id))
    onColumnOrderChange([...reordered, ...hidden])
  }

  const list = (
    <div className={useModal ? undefined : s.popoverConfigScroll}>
      {!useModal ? (
        <div className={s.popoverPanelTitle}>{t('shared.table.columns')}</div>
      ) : null}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleColumnDragEnd}
      >
        <SortableContext
          items={orderedForConfig.map((c) => c.id)}
          strategy={verticalListSortingStrategy}
        >
          {orderedForConfig.map((c) => (
            <SortableConfigRow
              key={c.id}
              id={c.id}
              label={getColumnLabel(c)}
              checked={columnVisibility[c.id] !== false}
              onToggle={() => toggleColumn(c.id)}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  )

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`${s.filterConfigBtn}${open ? ' is-active' : ''}`}
        aria-label={t('shared.filter.setColumns')}
        title={t('shared.filter.setColumns')}
        aria-expanded={open}
        aria-haspopup={useModal ? 'dialog' : 'menu'}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="grid" size={14} />
      </button>
      {useModal ? (
        <Modal
          open={open}
          setOpen={setOpen}
          minWidth="360px"
          maxWidth="420px"
          minHeight="auto"
          maxHeight="85vh"
          container={getPortalContainer()}
        >
          <Modal.Header>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
              Set Columns
            </h3>
          </Modal.Header>
          <Modal.Body>{list}</Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              color="primary"
              variant="solid"
              fullWidth
              onClick={() => setOpen(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      ) : (
        <TablePopover
          anchorRef={btnRef}
          open={open}
          onClose={() => setOpen(false)}
          align="start"
          className={s.popoverConfig}
        >
          {list}
        </TablePopover>
      )}
    </>
  )
}
